import TableWithTitle from "@/components/TableWithTitle.vue";
import WrapWithPopover from "@/components/shared/WrapWithPopover.vue";
import { getDescriptionBasedOnKpiKey, getColorsBasedOnRange, transformGoogleSheetFromCSV } from "@/utils/transformers";
import { SELECTOR, PERIOD_FIELD, REVENUE_EXCL_VAT_FIELD, DAYS_INVENTORY_FIELD, PAGE_VIEWS_FIELD, ORGANIC_CVR_FIELD, PPC_SALES_FIELD, TACOS_FIELD, BUYBOX_FIELD, TOTAL_QUANTITY_FIELD, LOST_REVENUE_EXCL_VAT_FIELD, AVERAGE_UNIT_PRICE_FIELD } from "@/utils/table_fields";
import AppIcon from "@/components/AppIcon.vue";
import { mapState } from "vuex";
import { HugValue, DateValue, RateChangeValue, CurrencyValue, NumberValue, BoxedValue, BarWithValue, PercentageValue, DottedValue, OverlapBarValue } from "@/components/tableElements";
import LiteTableMixin from "@/mixins/LiteTableMixin.vue";
import DownloadMixin from "@/mixins/DownloadMixin.vue";
import { DEFAULT_DATE_FORMATS, DAYS_INVENTORY_HEATMAP_COLORS, ORGANIC_CVR_HEATMAP_COLORS, BUYBOX_HEATMAP_COLORS, PERIOD_COLUMN_TITLES, APP_PERIOD_DROPDOWN_ELEMENTS, COLORS } from "@/utils/constants";
import FlexRow from "@/components/FlexRow.vue";
import moment from "moment";
import { capitalize } from "vue";
import SlotWithEvolutionValue from "@/components/tableElements/SlotWithEvolutionValue.vue";
import EvolutionValue from "@/components/tableElements/EvolutionValue.vue";
import WrapTableWithShowMore from "@/components/WrapTableWithShowMore.vue";
import TasksMixin from "@/mixins/TasksMixin.vue";
import EvolutionChartModal from "@/components/EvolutionChartModal.vue";
import LineChart from "@/components/charts/LineChart.vue";
import BarChart from "@/components/charts/BarChart.vue";
import BarLineChart from "@/components/charts/BarLineChart.vue";
import { transformEvolutionDataIntoChartData } from "../../utils/transformers";
import { processDate } from "../../utils";
import ChartsMixin from "@/mixins/ChartsMixin.vue";
import { EvolutionChartEngine } from "@/utils/dashboard/chart_engine";
import EvolutionTypeSwitcher from "@/components/EvolutionTypeSwitcher.vue";
export default {
  name: "PeriodTable",
  components: {
    TableWithTitle,
    WrapWithPopover,
    AppIcon,
    HugValue,
    DateValue,
    RateChangeValue,
    CurrencyValue,
    NumberValue,
    BoxedValue,
    BarWithValue,
    PercentageValue,
    DottedValue,
    OverlapBarValue,
    FlexRow,
    SlotWithEvolutionValue,
    EvolutionValue,
    WrapTableWithShowMore,
    EvolutionChartModal,
    LineChart,
    BarChart,
    BarLineChart,
    EvolutionTypeSwitcher
  },
  mixins: [LiteTableMixin, DownloadMixin, TasksMixin, ChartsMixin],
  props: {
    loading: {
      typ: Boolean,
      required: true
    }
  },
  emits: ["setDateRange", "refresh"],
  data() {
    return {
      chartModal: false,
      sheetGenerationTaskIdentifier: "",
      DAYS_INVENTORY_HEATMAP_COLORS,
      ORGANIC_CVR_HEATMAP_COLORS,
      BUYBOX_HEATMAP_COLORS,
      PERIOD_COLUMN_TITLES,
      APP_PERIOD_DROPDOWN_ELEMENTS,
      COLORS,
      fields: [SELECTOR, PERIOD_FIELD, REVENUE_EXCL_VAT_FIELD, TOTAL_QUANTITY_FIELD, AVERAGE_UNIT_PRICE_FIELD, DAYS_INVENTORY_FIELD, LOST_REVENUE_EXCL_VAT_FIELD, PAGE_VIEWS_FIELD, ORGANIC_CVR_FIELD, BUYBOX_FIELD, PPC_SALES_FIELD, TACOS_FIELD],
      csvSources: period => ({
        [PERIOD_COLUMN_TITLES[period]]: PERIOD_FIELD.key,
        [REVENUE_EXCL_VAT_FIELD.label]: REVENUE_EXCL_VAT_FIELD.key,
        ...this.getEvolutionsForField(REVENUE_EXCL_VAT_FIELD, {
          hasCurrencyEvolution: true
        }),
        [TOTAL_QUANTITY_FIELD.label]: TOTAL_QUANTITY_FIELD.key,
        ...this.getEvolutionsForField(TOTAL_QUANTITY_FIELD),
        [AVERAGE_UNIT_PRICE_FIELD.label]: AVERAGE_UNIT_PRICE_FIELD.key,
        ...this.getEvolutionsForField(AVERAGE_UNIT_PRICE_FIELD),
        [DAYS_INVENTORY_FIELD.label]: DAYS_INVENTORY_FIELD.key,
        [LOST_REVENUE_EXCL_VAT_FIELD.label]: LOST_REVENUE_EXCL_VAT_FIELD.key,
        [PAGE_VIEWS_FIELD.label]: PAGE_VIEWS_FIELD.key,
        [ORGANIC_CVR_FIELD.label]: ORGANIC_CVR_FIELD.key,
        [BUYBOX_FIELD.label]: BUYBOX_FIELD.key,
        [PPC_SALES_FIELD.label]: PPC_SALES_FIELD.key,
        [TACOS_FIELD.label]: TACOS_FIELD.key
      }),
      datasets: {},
      options: {},
      chartEngine: null
    };
  },
  watch: {
    period: {
      handler(newValue) {
        if (this.chartEngine && newValue) {
          this.chartEngine.chartPeriod.setPeriodOption(newValue);
        }
      },
      immediate: true
    },
    sortedItems: {
      handler(newItems, oldItems) {
        if (this.chartEngine && newItems) {
          this.chartConfig = this.chartEngine.transformData(newItems);
        }
      },
      immediate: true
    }
  },
  created() {
    this.chartEngine = new EvolutionChartEngine();
    this.chartEngine.setData(this.sortedItems, [REVENUE_EXCL_VAT_FIELD, null], this.getDateValueFormat, this.currency_config);
  },
  computed: {
    ...mapState({
      periodTable: state => state.dashboard_v2.periodTable,
      tab: state => state.dashboard_v2.currentTabValue,
      period: state => state.dashboard_v2.period,
      currentPeriod: state => state.dashboard_v2.currentPeriod,
      periodCategory: state => state.dashboard_v2.periodCategory,
      currency_config: state => state.currency_config.config
    }),
    chartConfig() {
      if (!this.chartEngine || !this.sortedItems?.length) {
        return {
          data: {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: false
              }
            },
            scales: {}
          }
        };
      }
      return this.chartEngine.transformData(this.sortedItems);
    },
    items() {
      return this.periodTable[this.tab].items;
    },
    sortedItems() {
      return [...this.items].sort((a, b) => moment(a.period_agg).isBefore(b.period_agg) ? -1 : 1);
    },
    maxCharCount() {
      return this.periodTable[this.tab].maxCharCount;
    },
    minMax() {
      return this.periodTable[this.tab].minMax;
    },
    getDateValueFormat() {
      // Convert the current period to UPPER CASE
      const period = this.period.toUpperCase();
      return DEFAULT_DATE_FORMATS[period];
    },
    getPeriodColumnTitle() {
      return PERIOD_COLUMN_TITLES[this.period];
    },
    csvFileName() {
      return `Dashboard_Performance_Evolution_${capitalize(this.period)}.csv`;
    },
    period_pager: {
      get() {
        return this.periodTable.period_page;
      },
      set(_) {
        this.$store.dispatch("dashboardV2IncrementPeriodPage");
        this.$emit("refresh");
      }
    },
    sortedItems() {
      return [...this.items].sort((a, b) => moment(a.period_agg).isBefore(b.period_agg) ? -1 : 1);
    }
  },
  methods: {
    getDescriptionBasedOnKpiKey,
    getColorsBasedOnRange,
    processDate,
    handleDateClick(period) {
      period = moment(period);
      this.$emit("setDateRange", period);
    },
    handleDateReset() {
      this.$emit("setDateRange", null);
    },
    async downloadPerformancePeriodCSV() {
      const csv_headers = this.csvSources(this.period);
      const csvFileName = this.csvFileName;
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.downloadCsv(str, csvFileName);
      this.trackDashboardCsvExport();
    },
    async copyPerformancePeriodCSV() {
      const csv_headers = this.csvSources(this.period);
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.copyCSV(str);
    },
    handleRowClick(item) {
      this.handleRowClicked(item, this.handleDateReset, this.handleDateClick, "period_agg");
    },
    async downloadPerformanceGoogleSheet() {
      const csv_headers = this.csvSources(this.period);
      const rowData = this.prepareCsvRowsForDownload(this.items, csv_headers);
      const data = transformGoogleSheetFromCSV(csv_headers, rowData);
      const task = await this.downloadCSVWithGoogleSheets("/dashboard/v2/generate/googlesheet", {
        sheet_name: this.csvFileName,
        task_type: "google_sheet_generation",
        data
      });

      //console.log('dashboard task', task);

      if (!task) {
        return;
      }
      this.sheetGenerationTaskIdentifier = task.task_id;
      this.executeTaskStatusHandler(task);
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleRunning() {
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleFailed(data) {
      this.exportLoading = false;
      if (this.asyncUpdateInterval) {
        clearTimeout(this.asyncUpdateInterval);
        this.asyncUpdateInterval = null;
      }
      window.$bus.trigger("showDanger", {
        visible: true,
        message: "Google sheet generation Task failed \n" + data.task_result,
        delay: 60 // Show alert less than 60 secs
      });
      throw new Error("Google sheet generation Task failed");
    },
    async handleFinished(data) {
      //console.log('dashboard finished', data);
      if (data) {
        const {
          task_result: {
            share_link
          }
        } = data;
        if (this.asyncUpdateInterval) {
          clearTimeout(this.asyncUpdateInterval);
          this.asyncUpdateInterval = null;
        }
        window.$bus.trigger("showSuccess", {
          visible: true,
          message: "The google sheet has been successfully generated. A link has been sent to your email.",
          delay: 60 // Show alert less than 60 secs
        });
        this.exportLoading = false;
        if (share_link) window.open(share_link, "_blank");
      }
    },
    onChartModalShown() {
      this.$nextTick(() => {
        this.$refs.chart?.chart?.resize();
      });
    },
    handleRechart(keys) {
      this.chartEngine.updateMetrics(keys);
    },
    switchEvolutionType($event) {
      const currentType = this.evolutionType === 'percentage' ? 'currency' : 'percentage';
      this.toggleEvolutionType($event, currentType);
    },
    refreshChart() {
      const chart = this.$refs?.chart;
      if (chart && chart.refreshChart) {
        chart.refreshChart();
      }
    },
    resizeChart(width, height) {}
  }
};